import React from 'react'
import { useTranslation } from 'react-i18next'
import ChatStatsDonut from './ChatStatsDonut'
import { CARD, SectionLabel } from './ChatStatsSection'
import { formatDelta, formatNumber, formatPercent } from './chatStatsFormat'

const TYPE_META = {
    teacher: { labelKey: 'chatStatsTypeTeacher', color: '#f2c94c' },
    counselor: { labelKey: 'chatStatsTypeCounsellor', color: '#f47c39' },
    sos: { labelKey: 'chatStatsTypeSos', color: '#689cfa' },
}

const BIG = { fontSize: 32, fontWeight: 800, color: '#2d2b45', lineHeight: 1 }
const LABEL = { fontSize: 13, color: '#8e8fb4', fontWeight: 600 }
const FOOT = { fontSize: 12, color: '#8e8fb4', marginTop: 12 }

const countOf = (rows, type) => rows?.find((row) => row.type === type)?.count ?? 0

function Insight({ delta, children }) {
    if (!delta) return null
    return (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, color: '#2d2b45', lineHeight: 1.45 }}>
            <strong style={{ color: delta.color, whiteSpace: 'nowrap' }}>{delta.text}</strong>
            <span>{children}</span>
        </div>
    )
}

function ChatStatsOverview({ stats }) {
    const { t } = useTranslation()
    const { adoption, by_type: byType, by_type_previous: byTypePrevious } = stats

    const totalDelta = formatDelta(adoption.delta_percent)
    const sosNow = countOf(byType, 'sos')
    const sosBefore = countOf(byTypePrevious, 'sos')
    const sosDelta = sosBefore ? formatDelta(Math.round(((sosNow - sosBefore) / sosBefore) * 1000) / 10) : null

    const segments = byType.map((entry) => ({ key: entry.type, percent: entry.percent, color: TYPE_META[entry.type]?.color || '#8e8fb4' }))

    return (
        <>
            <SectionLabel number={1}>{t('chatStatsSection1')}</SectionLabel>

            <div style={{ display: 'flex', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
                <div style={{ ...CARD, flex: 1, minWidth: 220 }}>
                    <div style={LABEL}>{t('chatStatsStudentsInitiated')}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 8 }}>
                        <span style={BIG}>{formatNumber(adoption.students_initiated)}</span>
                        <span style={{ fontSize: 13, color: '#8e8fb4' }}>
                            {t('chatStatsOfStudents', { total: formatNumber(adoption.students_total), percent: formatPercent(adoption.percent) })}
                        </span>
                    </div>
                    <div style={{ height: 7, background: '#e4e3f0', borderRadius: 999, overflow: 'hidden', marginTop: 12 }}>
                        <div style={{ height: '100%', width: `${Math.min(adoption.percent, 100)}%`, background: '#5e639b', borderRadius: 999 }} />
                    </div>
                </div>

                <div style={{ ...CARD, flex: 1, minWidth: 220 }}>
                    <div style={LABEL}>{t('chatStatsTotalChats')}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 8 }}>
                        <span style={BIG}>{formatNumber(adoption.total_chats)}</span>
                        {totalDelta && <span style={{ fontSize: 13, fontWeight: 700, color: totalDelta.color }}>{totalDelta.text}</span>}
                    </div>
                    <div style={FOOT}>{t('chatStatsVsPrevious', { count: formatNumber(adoption.total_chats_previous) })}</div>
                </div>

                <div style={{ ...CARD, flex: 1, minWidth: 220 }}>
                    <div style={LABEL}>{t('chatStatsAvgPerStudent')}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 8 }}>
                        <span style={BIG}>{adoption.avg_per_student}</span>
                        <span style={{ fontSize: 13, color: '#8e8fb4' }}>{t('chatStatsChatsEach')}</span>
                    </div>
                    <div style={FOOT}>{t('chatStatsPerDay', { count: adoption.per_day })}</div>
                </div>
            </div>

            <div style={{ ...CARD, padding: '22px 24px', marginBottom: 26 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#2d2b45' }}>{t('chatStatsByType')}</span>
                    <span style={{ background: '#e4e3f0', color: '#5e639b', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>
                        {t('chatStatsDaysBadge', { count: stats.period.days })}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 34, flexWrap: 'wrap' }}>
                    <ChatStatsDonut segments={segments} total={formatNumber(adoption.total_chats)} />

                    <div style={{ flex: 'none', width: 220, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {byType.map((entry) => (
                            <div key={entry.type} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                <span style={{ width: 12, height: 12, borderRadius: 4, background: TYPE_META[entry.type]?.color, flex: 'none' }} />
                                <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#5e639b' }}>
                                    {TYPE_META[entry.type] ? t(TYPE_META[entry.type].labelKey) : entry.type}
                                </span>
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#2d2b45' }}>{formatNumber(entry.count)}</span>
                                <span style={{ fontSize: 13, color: '#8e8fb4', width: 42, textAlign: 'right' }}>{formatPercent(entry.percent)}</span>
                            </div>
                        ))}
                    </div>

                    <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <Insight delta={totalDelta}>{t(totalDelta?.rising ? 'chatStatsInsightMoreChats' : 'chatStatsInsightFewerChats')}</Insight>
                        <Insight delta={sosDelta}>{t(sosDelta?.rising ? 'chatStatsInsightMoreSos' : 'chatStatsInsightFewerSos')}</Insight>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ChatStatsOverview
